
import {
  Component, Ref, Mixins, Watch, Prop, PropSync, Inject,
} from 'vue-property-decorator';
import RequiredFieldsWarning from '@/features/common/components/RequiredFieldsWarning.vue';
// import MultipleInput from '@/features/common/components/MultipleInput.vue';
import { VForm } from '@/features/common/types';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import { UserPreferences } from '@/features/settings/types';
import { signatureOptions } from '@/features/common/raw';
import { InputValidationRules } from 'vuetify';
import { requiredRules, emailRules } from '@/features/common/helpers/validationRules';
import { getIsFreight } from '@/features/notifications/helpers/createShipment';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { scrollToBottom, scrollToInPage } from '@/features/common/helpers';
import { EstimateDataType } from '@/features/estimate/types';
import {
  rulesIfAttempted as rulesIfAttemptedFn,
  CreateShipmentValidationState,
} from '@/features/shipment/helpers/createShipmentValidation';
import FreightOptions from './FreightOptions.vue';
import ReturnShipmentOptions from './ReturnShipmentOptions.vue';
import { InProgress, ShipmentExtras } from '../../types';

export interface FormValidationType {
  isValid: boolean,
  message: string,
  isHint: boolean,
}

@Component({
  components: {
    RequiredFieldsWarning,
    FreightOptions,
    ReturnShipmentOptions,
    // MultipleInput,
  },
})
export default class ExtrasShipmentForm extends Mixins(ResponsivenessMixin) {
  @Inject({
    from: 'createShipmentValidationState',
    default: () => ({ attempted: true }),
  })
  private createShipmentValidationState!: CreateShipmentValidationState;

  @Ref('formExtrasShipment') formExtrasShipment!: VForm;

  // this flag will determine whether to create shipment or just estimate
  @Prop({ required: false, default: false }) isJustEstimate!: boolean;

  @Prop({ required: false, default: false }) isEstimateInternational!: boolean;

  @PropSync('isLoading', { required: false, default: false }) isLoadingNext!: boolean;

  @PropSync('validationMessage', {
    default: () => ({
      isValid: true,
      message: '',
      isHint: true,
    }),
  }) formValidationMsg!: FormValidationType;

  private signatures: Array<{ text: string, value: string }> = signatureOptions;

  private isReferenceMandatory = true;

  private requiredRules = requiredRules;

  // private phoneNumberInvalidRules = phoneNumberInvalidRules;

  private extras: ShipmentExtras = {
    signature: 'None',
    reference: '',
    comments: '',
  };

  private isDocumentsOnly = false;

  private isLabelFree = false;

  private isStandardReturn = false;

  private isLabelFreeBoxFree = false;

  private emailNotifications: {
    email: string,
    list: string[],
    rules: InputValidationRules
  } = {
    email: '',
    list: [],
    rules: [...emailRules],
  }

  private isFormChanged = false;

  private isRequirementsMet = false;

  private rulesIfAttempted(rules?: InputValidationRules): InputValidationRules {
    return rulesIfAttemptedFn(rules, this.createShipmentValidationState.attempted);
  }

  get isSubmitButtonDisabled(): boolean {
    return this.isLoadingNext
      || (this.isReturnsWithCanada && !this.isStandardReturn && !this.isRequirementsMet);
  }

  get isInProgressData(): number {
    return this.$store.getters.getIsInProgressData;
  }

  get inProgressData(): InProgress {
    return this.$store.state.shipment.inProgress;
  }

  get isUserPreferences(): number {
    return this.$store.getters.getIsUserPreferences;
  }

  get userPreferences(): UserPreferences {
    return this.$store.getters.getUserPreferences;
  }

  get estimateData(): EstimateDataType {
    return this.$store.state.estimate.estimateData;
  }

  get referenceRules(): InputValidationRules {
    return this.isReferenceMandatory
      ? [...this.requiredRules]
      : [];
  }

  get isInternationalShipment(): boolean {
    if (this.isJustEstimate) return this.isEstimateInternational;
    return this.$store.getters.getIsInternational;
  }

  get isCommercialInvoiceNeeded(): boolean {
    return this.$store.getters.getIsCommercialInvoice;
  }

  get isFreightOptionsEnabled(): boolean {
    if (this.isJustEstimate) {
      const { packages } = this.estimateData;
      return getIsFreight(packages);
    }
    return getIsFreight(this.inProgressData.packages);
  }

  get isReturnsWithCanada(): boolean {
    const {
      isLabelFree, isBoxFree, isReturnShipment, toAddress, fromAddress,
    } = this.inProgressData || {};

    if (isLabelFree || isBoxFree || isReturnShipment) return true;

    const isWithinCanada = fromAddress.countryCode === 'CA' && toAddress.countryCode === 'CA';

    const { shippingSettings } = this.userPreferences || {};
    const { defaultAddress } = shippingSettings || {};

    if (isWithinCanada && toAddress.postalCode === defaultAddress?.postalCode) {
      this.isStandardReturn = true;
      this.updateInProgressData(true, 1200);
      return true;
    }

    return false;
  }

  @Watch('isUserPreferences', { immediate: true })
  private populateUserPrefsData(newValue: number): void {
    if (!newValue) return;

    const {
      signatureOption: prefSignatureOption,
      isReferenceMandatory,
      reference: prefReference,
      comments: prefComments,
    } = this.userPreferences.shippingSettings;

    this.extras.signature = prefSignatureOption || 'None';
    this.extras.reference = prefReference || '';
    this.extras.comments = prefComments || '';

    this.isReferenceMandatory = isReferenceMandatory;
    this.isDocumentsOnly = false;

    if (!this.isJustEstimate) return;

    const {
      isDocumentsOnly,
      signatureOptions: estimateSignatures,
    } = this.estimateData;

    if (isDocumentsOnly) {
      this.isDocumentsOnly = isDocumentsOnly;
    }

    if (estimateSignatures) {
      this.extras.signature = estimateSignatures || prefSignatureOption;
    }

    this.updateSignatureOption();
  }

  @Watch('isInProgressData', { immediate: true })
  private replaceShipmentData(newValue: number): void {
    if (!newValue) return;
    if (this.isJustEstimate) return;

    const {
      comments,
      signatureOptions: signature,
      documentsOnly, emailNotifications, references,
      isReturnShipment, isLabelFree, isBoxFree,
    } = this.inProgressData;

    const [reference] = references || [];

    this.extras = {
      signature: signature || 'None',
      reference: reference || '',
      comments: comments || '',
    };

    this.isDocumentsOnly = !!documentsOnly;
    this.isLabelFree = !!isLabelFree && !isBoxFree;
    this.isLabelFreeBoxFree = !!isLabelFree && !!isBoxFree;
    this.isStandardReturn = !!isReturnShipment && !this.isLabelFree && !this.isLabelFreeBoxFree;

    if (emailNotifications && emailNotifications.length) {
      this.emailNotifications.list = emailNotifications.map((email) => email.email.emailAddress);
      [this.emailNotifications.email] = this.emailNotifications.list; // no need for multiples
    }
  }

  created(): void {
    eventBus.$on(eventNames.SHIPMENT_SUBMIT, this.submitManual);
  }

  // private addEmailNotification() {
  //   this.emailNotifications.list.push(this.emailNotifications.email);
  //   this.emailNotifications.email = '';
  //   this.saveEmailNotificationsInProgress();
  // }

  // private deleteEmailNotification(idx: number) {
  //   this.emailNotifications.list.splice(idx, 1);
  //   this.saveEmailNotificationsInProgress();
  // }

  // private saveEmailNotificationsInProgress() {
  //   this.updateInProgressData(
  //     'emailNotifications',
  //     this.emailNotifications.list.map((item) => ({
  //       email: {
  //         emailAddress: item,
  //       },
  //       delivery: true,
  //       exception: false,
  //       ship: false,
  //     })),
  //     true,
  //   );
  // }

  private updateSignatureOption() {
    if (this.isJustEstimate) {
      this.$store.commit('patchEstimateData', {
        key: 'signatureOptions',
        value: this.extras.signature,
      });
      eventBus.$emit(eventNames.UPDATE_PACKAGES_SIGNATURE, this.extras.signature);
      return;
    }

    this.$store.commit('updateInProgressPackagesSignature', this.extras.signature);
    this.updateInProgressData(true);
  }

  private async updateInProgressData(force = false, delay = 0) {
    if (!force && !this.isFormChanged) return;

    const {
      comments, reference, signature,
    } = this.extras;

    const payload: InProgress = {
      ...this.inProgressData,
      signatureOptions: signature,
      comments,
      references: [reference],
      emailNotifications: [{
        email: {
          emailAddress: this.emailNotifications.email,
        },
        delivery: true,
        exception: false,
        ship: false,
      }],
      documentsOnly: this.isDocumentsOnly,
      isLabelFree: this.isLabelFree || this.isLabelFreeBoxFree,
      isBoxFree: this.isLabelFreeBoxFree,
      isReturnShipment: this.isStandardReturn || this.isLabelFree || this.isLabelFreeBoxFree,
    };

    if (delay) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    this.$store.dispatch('setAndSyncInProgressData', payload);
  }

  private handleIsDocumentsOnlyShipment() {
    if (this.isJustEstimate) {
      this.$store.commit('patchEstimateData', {
        key: 'isDocumentsOnly',
        value: this.isDocumentsOnly,
      });
      return;
    }

    this.updateInProgressData(true);
  }

  private handleUpdateReturnOption() {
    if (this.isJustEstimate) return;
    this.updateInProgressData(true);
  }

  private isExtrasShipmentFormValid() {
    const form = this.formExtrasShipment;
    if (!form) return false;

    return form.validate();
  }

  private async submit(): Promise<boolean> {
    this.isLoadingNext = true;
    await new Promise((resolve) => setTimeout(resolve, 600));
    this.isLoadingNext = false;

    this.createShipmentValidationState.attempted = true;
    await this.$nextTick();

    const isExtrasFormValid = this.isExtrasShipmentFormValid();

    if (isExtrasFormValid) {
      (this.$refs.formExtrasShipment as HTMLFormElement).resetValidation();
    }

    if (isExtrasFormValid && !this.isJustEstimate) {
      this.updateInProgressData(true);
    }

    this.$emit('submit', isExtrasFormValid, this.formExtrasShipment);
    this.$emit('next');

    return isExtrasFormValid;
  }

  private async submitManual() {
    const isSuccess = await this.submit();
    if (isSuccess) return;

    scrollToBottom();
  }

  private handleClearAllInformation(): void {
    const dialogData = {
      show: true,
      btnText: this.$t('buttons.ok') as string,
      content: this.$t('alertDialog.areYouSureYouWantToClear'),
      title: this.$t('alertDialog.areYouSure'),
    };
    this.$store.commit('toggleConfirmDialog', dialogData);
    eventBus.$once(eventNames.CONFIRMATION_DIALOG, this.clearAllInProgressInfo);
  }

  private clearAllInProgressInfo() {
    this.createShipmentValidationState.attempted = false;
    (this.$refs.formExtrasShipment as HTMLFormElement).resetValidation();

    if (this.isJustEstimate) {
      eventBus.$emit(eventNames.CLEAR_ESTIMATE_FORM);
      this.resetFormData();
    } else {
      eventBus.$emit(eventNames.SHIPMENT_CLEAR_ALL_INPUTS);
      this.$store.dispatch('clearAllInformation');
    }

    this.resetShipmentState();

    scrollToInPage(0, 1, true);
    this.showValidationMessage({
      isValid: true,
      message: '',
      isHint: true,
    });
    this.isFormChanged = false;
  }

  private resetShipmentState() {
    this.emailNotifications = {
      email: '',
      list: [],
      rules: [...emailRules],
    };

    this.$store.commit('setAvailableServices', null);
    this.$store.commit('setReconnectAccountRequired', undefined);
    this.$store.commit('setSelectedService', null);
  }

  private showValidationMessage(payload: FormValidationType) {
    this.formValidationMsg = payload;
  }

  private resetFormData() {
    this.isDocumentsOnly = false;
    this.populateUserPrefsData(Date.now());
  }
}
